import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Download, MoveRight, ArrowRightLeft, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/App';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { formatCurrency, SortableHeader, sortAndFilter } from './helpers';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const LaborTab = ({ labor, data, selectedLaborIds, setSelectedLaborIds, onMoveClick, jobId }) => {
  const { token } = useAuth();
  const [sort, setSort] = useState({ field: 'date', dir: 'desc' });
  const [filter, setFilter] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [dailyPdfLoading, setDailyPdfLoading] = useState(false);
  const [dailyDate, setDailyDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Week picker: default to current week's Monday
  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.setDate(diff));
  };
  const [weekStart, setWeekStart] = useState(() => {
    const mon = getMonday(new Date());
    return mon.toISOString().split('T')[0];
  });

  const weekEnd = (() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 6);
    return d;
  })();

  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d.toISOString().split('T')[0]);
  };
  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d.toISOString().split('T')[0]);
  };

  const formatWeekLabel = () => {
    const start = new Date(weekStart + 'T00:00:00');
    const end = weekEnd;
    const opts = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', opts)} - ${end.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
  };

  const exportWeeklyPdf = async () => {
    if (!jobId || !token) return;
    setPdfLoading(true);
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/weekly-timesheet-pdf?weekStart=${weekStart}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Timesheet_${data?.job?.jobNumber || 'JOB'}_${weekStart}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  const exportDailyPdf = async () => {
    if (!jobId || !token) return;
    setDailyPdfLoading(true);
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/daily-timesheet-pdf?date=${dailyDate}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `DailyTimesheet_${data?.job?.jobNumber || 'JOB'}_${dailyDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Daily PDF export failed:', err);
    } finally {
      setDailyPdfLoading(false);
    }
  };

  const sortedEntries = sortAndFilter(labor?.entries, sort, filter, ['fullName', 'classificationName', 'date']);

  const toggleSelection = (entryId) => {
    setSelectedLaborIds(prev =>
      prev.includes(entryId) ? prev.filter(id => id !== entryId) : [...prev, entryId]
    );
  };

  const selectAll = () => {
    if (!data?.labor?.entries) return;
    setSelectedLaborIds(data.labor.entries.map(e => e.id));
  };

  const deselectAll = () => setSelectedLaborIds([]);

  const exportToExcel = () => {
    if (!labor?.entries?.length) return;
    const exportData = labor.entries.map(entry => ({
      'Date': entry.date,
      'Crew Member': entry.crewMemberName,
      'Craft': entry.craftName || '',
      'Classification': entry.classificationName,
      'Shift': entry.shift || 'Day',
      'ST Hours': entry.hoursST,
      'OT Hours': entry.hoursOT,
      'DT Hours': entry.hoursDT,
      'Total Hours': entry.totalHours,
      'Total Charge': entry.totalCharge
    }));
    exportData.push({
      'Date': 'TOTAL', 'Crew Member': '', 'Craft': '', 'Classification': '', 'Shift': '',
      'ST Hours': labor.totalHoursST || 0, 'OT Hours': labor.totalHoursOT || 0,
      'DT Hours': labor.totalHoursDT || 0, 'Total Hours': labor.totalHours || 0,
      'Total Charge': labor.total || 0
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Labor');
    XLSX.writeFile(wb, `${data.job.jobNumber}_labor_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Card className='border-slate-200 shadow-sm rounded-sm'>
      <CardHeader>
        <div className='flex items-center justify-between flex-wrap gap-4'>
          <CardTitle className='text-xl font-bold text-slate-800'>
            Labor Breakdown
            <span className='ml-2 text-lg font-normal text-slate-500'>Total: {formatCurrency(labor.total)}</span>
          </CardTitle>
          <div className='flex items-center gap-2'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400' />
              <Input placeholder='Search labor...' value={filter} onChange={(e) => setFilter(e.target.value)}
                className='pl-9 w-48 rounded-sm' data-testid="labor-search" />
            </div>
            {selectedLaborIds.length > 0 && (
              <Button variant='outline' size='sm' onClick={onMoveClick}
                className='rounded-sm border-2 border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white'
                data-testid="move-labor-btn">
                <MoveRight className='w-4 h-4 mr-2' />
                Move Selected ({selectedLaborIds.length})
              </Button>
            )}
            <Button variant='outline' size='sm' onClick={exportToExcel} disabled={!labor?.entries?.length}
              className='rounded-sm border-2 border-[#1A3668] text-[#1A3668] hover:bg-[#1A3668] hover:text-white'
              data-testid="export-job-labor-excel">
              <Download className='w-4 h-4 mr-2' />
              Excel
            </Button>
          </div>
        </div>
        {/* Weekly Timesheet PDF Export */}
        <div className='flex items-center justify-between flex-wrap gap-3 mt-4 p-3 bg-slate-50 border border-slate-200 rounded-sm'>
          <div className='flex items-center gap-2'>
            <FileText className='w-4 h-4 text-[#1A3668]' />
            <span className='text-sm font-semibold text-slate-700'>Weekly Timesheet PDF</span>
          </div>
          <div className='flex items-center gap-2'>
            <Button variant='ghost' size='sm' onClick={prevWeek} className='p-1 h-8 w-8' data-testid="week-prev">
              <ChevronLeft className='w-4 h-4' />
            </Button>
            <span className='text-sm font-medium text-slate-700 min-w-[180px] text-center' data-testid="week-label">
              {formatWeekLabel()}
            </span>
            <Button variant='ghost' size='sm' onClick={nextWeek} className='p-1 h-8 w-8' data-testid="week-next">
              <ChevronRight className='w-4 h-4' />
            </Button>
            <Button size='sm' onClick={exportWeeklyPdf} disabled={pdfLoading}
              className='rounded-sm bg-[#1A3668] text-white hover:bg-[#142A52] ml-2'
              data-testid="export-weekly-pdf">
              <FileText className='w-4 h-4 mr-2' />
              {pdfLoading ? 'Generating...' : 'Export PDF'}
            </Button>
          </div>
        </div>
        {/* Daily Timesheet PDF Export */}
        <div className='flex items-center justify-between flex-wrap gap-3 mt-2 p-3 bg-slate-50 border border-slate-200 rounded-sm'>
          <div className='flex items-center gap-2'>
            <FileText className='w-4 h-4 text-[#1A3668]' />
            <span className='text-sm font-semibold text-slate-700'>Daily Timesheet PDF</span>
          </div>
          <div className='flex items-center gap-2'>
            <Input type='date' value={dailyDate} onChange={(e) => setDailyDate(e.target.value)}
              className='rounded-sm border-2 h-8 text-sm w-[160px]' data-testid="daily-date-picker" />
            <Button size='sm' onClick={exportDailyPdf} disabled={dailyPdfLoading}
              className='rounded-sm bg-[#1A3668] text-white hover:bg-[#142A52]'
              data-testid="export-daily-pdf">
              <FileText className='w-4 h-4 mr-2' />
              {dailyPdfLoading ? 'Generating...' : 'Export PDF'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {sortedEntries.length > 0 && (
          <div className='flex items-center gap-4 mb-4 p-3 bg-slate-50 rounded-sm'>
            <span className='text-sm text-slate-600'>Select labor entries to move:</span>
            <Button variant='ghost' size='sm' onClick={selectAll} className='text-[#1A3668] text-xs'>Select All</Button>
            <Button variant='ghost' size='sm' onClick={deselectAll} className='text-slate-500 text-xs' disabled={selectedLaborIds.length === 0}>Deselect All</Button>
            {selectedLaborIds.length > 0 && (
              <span className='text-sm font-medium text-amber-600'>
                {selectedLaborIds.length} selected ({formatCurrency(sortedEntries.filter(e => selectedLaborIds.includes(e.id)).reduce((sum, e) => sum + e.totalCharge, 0))})
              </span>
            )}
          </div>
        )}

        <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-slate-50 rounded-sm'>
          <div>
            <p className='text-xs font-bold uppercase text-slate-500'>ST Hours</p>
            <p className='text-xl font-bold text-slate-700'>{labor.totalHoursST?.toFixed(1) || 0}</p>
          </div>
          <div>
            <p className='text-xs font-bold uppercase text-slate-500'>OT Hours</p>
            <p className='text-xl font-bold text-amber-600'>{labor.totalHoursOT?.toFixed(1) || 0}</p>
          </div>
          <div>
            <p className='text-xs font-bold uppercase text-slate-500'>DT Hours</p>
            <p className='text-xl font-bold text-red-600'>{labor.totalHoursDT?.toFixed(1) || 0}</p>
          </div>
          <div>
            <p className='text-xs font-bold uppercase text-slate-500'>Total Hours</p>
            <p className='text-xl font-bold text-[#1A3668]'>{labor.totalHours?.toFixed(1) || 0}</p>
          </div>
        </div>

        <div className='overflow-x-auto'>
          <table className='w-full border-collapse text-sm'>
            <thead>
              <tr className='bg-slate-100 border-b-2 border-slate-200'>
                <th className='p-2 w-10'>
                  <Checkbox checked={selectedLaborIds.length === sortedEntries.length && sortedEntries.length > 0}
                    onCheckedChange={(checked) => checked ? selectAll() : deselectAll()} data-testid="select-all-labor" />
                </th>
                <SortableHeader field='date' label='Date' sortState={sort} setSortState={setSort} />
                <SortableHeader field='fullName' label='Name' sortState={sort} setSortState={setSort} />
                <SortableHeader field='craftName' label='Craft' sortState={sort} setSortState={setSort} />
                <SortableHeader field='classificationName' label='Classification' sortState={sort} setSortState={setSort} />
                <SortableHeader field='hoursST' label='ST' sortState={sort} setSortState={setSort} align='right' />
                <SortableHeader field='hoursOT' label='OT' sortState={sort} setSortState={setSort} align='right' />
                <SortableHeader field='hoursDT' label='DT' sortState={sort} setSortState={setSort} align='right' />
                <SortableHeader field='totalHours' label='Total Hrs' sortState={sort} setSortState={setSort} align='right' />
                <SortableHeader field='totalCharge' label='Charges' sortState={sort} setSortState={setSort} align='right' />
                <th className='p-2 text-left text-sm font-semibold text-slate-600'>Source</th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map((entry) => (
                <tr key={entry.id} className={`border-b border-slate-200 hover:bg-slate-50 ${selectedLaborIds.includes(entry.id) ? 'bg-amber-50' : ''} ${entry.isCrossCharge ? 'bg-blue-50/50' : ''}`}>
                  <td className='p-2'>
                    <Checkbox checked={selectedLaborIds.includes(entry.id)} onCheckedChange={() => toggleSelection(entry.id)}
                      data-testid={`select-labor-${entry.id}`} />
                  </td>
                  <td className='p-2 font-mono text-slate-600'>{entry.date}</td>
                  <td className='p-2 font-medium text-slate-700'>{entry.fullName}</td>
                  <td className='p-2 text-slate-600'>{entry.craftName}</td>
                  <td className='p-2 text-slate-600'>{entry.classificationName}</td>
                  <td className='p-2 text-right font-mono text-slate-700'>{entry.hoursST.toFixed(1)}</td>
                  <td className='p-2 text-right font-mono text-amber-600'>{entry.hoursOT.toFixed(1)}</td>
                  <td className='p-2 text-right font-mono text-red-600'>{entry.hoursDT.toFixed(1)}</td>
                  <td className='p-2 text-right font-mono font-bold text-slate-700'>{entry.totalHours.toFixed(1)}</td>
                  <td className='p-2 text-right font-mono font-bold text-[#16A34A]'>{formatCurrency(entry.totalCharge)}</td>
                  <td className='p-2'>
                    {entry.isCrossCharge && (
                      <Badge variant='outline' className='text-xs border-blue-300 text-blue-700 bg-blue-50 whitespace-nowrap'>
                        <ArrowRightLeft className='w-3 h-3 mr-1' />
                        From {entry.crossChargeSource?.sourceJobNumber}
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sortedEntries.length === 0 && (
            <div className='text-center p-8 text-slate-500'>
              {filter ? 'No matching labor entries' : 'No labor entries for this job'}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
